import { useParams } from "wouter";
import { trpc } from "@/lib/trpc";
import { Loader2 } from "lucide-react";
import { InvoiceView } from "@/components/invoice/InvoiceView";
import { formatCardPaymentUnavailableMessage } from "@shared/invoicePayment";

export default function PortalInvoicePage() {
  const { token } = useParams<{ token: string }>();
  const { data, isLoading, error } = trpc.financeInvoices.getPublicPortalInvoice.useQuery(
    { token: token ?? "" },
    { enabled: Boolean(token) }
  );
  const getPdf = trpc.financeInvoices.getPublicPortalInvoicePdf.useMutation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 text-center text-sm text-gray-500">
        Invoice not found or the link is no longer valid.
      </div>
    );
  }
  const subtotal = data.gstApplied ? Number((data.amount / 1.09).toFixed(2)) : data.amount;
  const gstAmount = data.gstApplied ? Number((data.amount - subtotal).toFixed(2)) : 0;

  return (
    <InvoiceView
      vm={{
        invoiceNumber: data.invoiceNumber,
        invoiceStatus: data.status,
        invoiceDate: data.invoiceDate,
        paidAt: data.paidAt ?? null,
        dueDate: data.dueDate ?? null,
        paymentTerms: data.paymentTerms ?? null,
        proposalRef: data.sourceRef ?? null,
        paymentStageLabel: data.sourceLabel ?? (data.sourceType ? `Source: ${data.sourceType}` : "Tax invoice"),
        customerName: data.customerName ?? "Customer",
        customerContactPerson: data.customerContact ?? null,
        billingAddress: data.billingAddress ?? null,
        customerPhone: data.customerPhone ?? null,
        customerEmail: data.customerEmail ?? null,
        picName: data.picName ?? "Clarity",
        picPhone: data.picPhone ?? null,
        sourceRef: data.sourceRef ?? null,
        sourceLine: data.sourceLine ?? null,
        description: data.description,
        lineItems: (data.lineItems ?? []).map((li) => ({
          description: li.description,
          quantity: li.quantity ?? 1,
          qty: li.quantity ?? 1,
          unitAmount: li.unitAmount,
          total: li.total,
        })),
        sourceLabel: data.sourceLabel ?? null,
        approvedProposalUrl: data.originalProposalPublicUrl ?? null,
        approvedProposalFileName: data.originalProposalFileName ?? null,
        gstApplied: Boolean(data.gstApplied),
        subtotal,
        gstAmount,
        total: Number(data.amount),
        amountPaidInclGst: (data as { amountPaidInclGst?: number }).amountPaidInclGst,
        creditAppliedAmount: data.creditAppliedAmount,
        paymentOnHold: data.paymentOnHold,
        amountOutstandingInclGst: (data as { amountOutstandingInclGst?: number }).amountOutstandingInclGst,
        payNowReference: data.invoiceNumber,
        paymentLink: data.paymentLink ?? null,
        cardPaymentLink: data.paymentLink ?? null,
        cardPaymentUnavailableMessage: !data.paymentLink
          ? formatCardPaymentUnavailableMessage(data.picName)
          : null,
        hasPdf: Boolean(data.hasPdf),
        onDownloadPdf: async () => {
          const r = await getPdf.mutateAsync({ token: token! });
          if (r?.url) window.open(r.url, "_blank", "noopener,noreferrer");
        },
      }}
    />
  );
}
